import { isAxiosError, type AxiosResponse } from 'axios';
import type { IApiValidationErrorResponse } from '@/types/axiosApi';
import type { ComputedRef } from 'vue';

export const isApiValidationErrorResponse = (
  response?: AxiosResponse
): response is AxiosResponse<IApiValidationErrorResponse> => {
  return !!response && response.status === 422;
};

export const formatApiValidationErrors = (
  response: AxiosResponse<IApiValidationErrorResponse>
): string => {
  return typeof response.data.errors === 'string'
    ? response.data.errors
    : Array.isArray(response.data.errors)
      ? response.data.errors.reduce(
          (acc, curr) =>
            `${acc}${acc !== '' ? ', ' : ''}${
              Array.isArray(curr)
                ? curr.join(', ')
                : typeof curr === 'string'
                  ? curr
                  : Object.values(curr).reduce(
                      (acc, curr) => `${acc}${acc !== '' ? ', ' : ''}${curr}`,
                      ''
                    )
            }`,
          ''
        )
      : Object.values(response.data.errors).join(', ');
};

export const getApiErrorByKey = (
  key: string,
  currentErrorMessage: ComputedRef<Error | null>
): null | string => {
  if (!currentErrorMessage.value) {
    return null;
  }

  if (isAxiosError(currentErrorMessage.value)) {
    if (isApiValidationErrorResponse(currentErrorMessage.value.response)) {
      if (typeof currentErrorMessage.value.response.data.errors === 'string') {
        return currentErrorMessage.value.response.data.errors;
      }
      if (currentErrorMessage.value.response?.data.errors[key]?.length) {
        return currentErrorMessage.value.response?.data.errors[key][0] ?? null;
      }
      return null;
    }
    return currentErrorMessage.value.response?.data.message;
  }
  return currentErrorMessage.value.message;
};

export const isApiBlobResponse = (response?: AxiosResponse): response is AxiosResponse<Blob> => {
  return !!response && response.data instanceof Blob;
};

export const parseBlobResponse = async (
  error: AxiosResponse<Blob>
): Promise<Record<string, unknown>> => {
  const reader: FileReader = new FileReader();
  reader.readAsText(error.data);
  return new Promise((resolve, reject) => {
    reader.onload = () => {
      try {
        resolve(JSON.parse(reader.result as string));
      } catch {
        resolve({ message: reader.result });
      }
    };
    reader.onerror = () => {
      reject(new Error('Failed to read error blob'));
    };
  });
};
